import { CaretDownOutlined } from "@ant-design/icons"
import { Company, InboxInvoice, OcrStatusEnum, RightEnum } from "@finway-group/shared/lib/models"
import { Employee } from "@finway-group/shared/lib/models/user/employee.model"
import { Button, Dropdown, Menu } from "antd"
import React, { Key, useCallback, useState } from "react"
import { UserPlus as Assign, Download as DownloadIcon, ArrowRight as Move, UserMinus as UnAssign } from "react-feather"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { CircleArrowIcon } from "~Components/icons/CircleArrowIcon"
import { EyeNoBgIcon } from "~Components/icons/EyeNoBgIcon"
import { TrashIcon } from "~Components/icons/TrashIcon"
import { EmployeeList, EmployeeListProps } from "~Components/select/employeeList.select"
import { SubCompanyList } from "~Components/select/subCompanyList.select"
import { useCompanies, useUserCompanies } from "~Shared/hooks/company.hooks"
import { useEmployeesWithEditingRights } from "~Shared/hooks/employee.hooks"
import { useRolesMap } from "~Shared/hooks/role.hooks"
import { useActiveCompanyProfile } from "~Shared/hooks/user.hooks"
import { AuthzService, NotificationService, UserService } from "~Shared/services"
import { InboxInvoiceService } from "~Shared/services/inboxInvoice.service"
import { NotificationDurationSec, NotificationTypeEnum } from "~Shared/services/notification.service"
import { moveOneInboxInvoice } from "~Shared/store/actions/inboxInvoice/inboxInvoiceActions"

export const enum InboxInvoiceOptionsEnum {
    REMOVE,
    PREVIEW,
    DOWNLOAD,
    MOVE,
    RETRY_OCR,
    ASSIGN,
    UNASSIGN,
}

type OnSelectFunc = (inboxInvoice: InboxInvoice) => ({ key, domEvent }: { key: Key; domEvent: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> }) => Promise<void>

interface InboxInvoiceDropdownProps {
    inboxInvoice: InboxInvoice
    onSelect: OnSelectFunc
}

export const InboxInvoiceDropdown = ({ inboxInvoice, onSelect }: InboxInvoiceDropdownProps) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const companies = useCompanies()
    const loggedInEmployee = UserService.getLoggedInEmployeeProfile()
    const activeCompany = useActiveCompanyProfile()
    const [isInvoiceActionDropdownOpen, setIsInvoiceActionDropdownOpen] = useState(false)
    const rolesMap = useRolesMap({ excludeDeleted: true })
    const possibleAssignees = useEmployeesWithEditingRights({ excludeDeleted: true, rolesMap })

    const employeeKeySelector = (employee: Employee) => employee.getFullName()
    const menuKeySelector = (company: Company) => company.name
    const handleSubMenuTitleClick = (e: { domEvent: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> }) => {
        e.domEvent.stopPropagation()
    }

    const canWriteInboxInvoice = AuthzService.isRightGrantedForLoggedInUser(RightEnum.INBOX_INVOICE__ALL__WRITE)
    const canAssignInboxInvoice = AuthzService.isRightGrantedForLoggedInUser(RightEnum.INBOX_INVOICE__ALL__ASSIGN)
    const userCompanies = useUserCompanies(companies, loggedInEmployee, activeCompany)

    const onEmployeeSelect = useCallback(
        async (employee: Employee) => {
            setIsInvoiceActionDropdownOpen(false)
            await InboxInvoiceService.assignInvoiceToUser(inboxInvoice, employee, t)
        },
        [inboxInvoice, t, setIsInvoiceActionDropdownOpen],
    )

    const moveInvoice = useCallback(
        async (inboxInvoice: InboxInvoice, company: Company) => {
            const dispatchFunction = moveOneInboxInvoice(inboxInvoice.id, company._id)
            try {
                const data = await dispatchFunction(dispatch)
                NotificationService.send(
                    NotificationTypeEnum.SUCCESS,
                    t("notification:invoice_inbox.move.title"),
                    t("notification:invoice_inbox.move.message", { name: data.fileName, company: company.name }),
                    NotificationDurationSec.DEFAULT,
                )
            } catch (err) {
                NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:invoice_inbox.move.title"))
            }
        },
        [t, dispatch],
    )

    const dropDownOverlay = (onEmployeeSelect: EmployeeListProps["onEmployeeSelect"]) => () => {
        const MenuItemContent = ({ icon, text }: { icon: JSX.Element; text: string }) => (
            <div className="flex items-center gap-8 text-text min-w-190 h-22">
                {icon}
                <span className="leading-none">{text}</span>
            </div>
        )
        return (
            <Menu onClick={onSelect(inboxInvoice)} triggerSubMenuAction="click">
                {inboxInvoice.ocrStatus === OcrStatusEnum.FAILED && canWriteInboxInvoice && (
                    <Menu.Item key={InboxInvoiceOptionsEnum.RETRY_OCR}>
                        <MenuItemContent icon={<CircleArrowIcon size={14} />} text={t("action:inbox_invoice.retry_ocr")} />
                    </Menu.Item>
                )}

                <Menu.Item key={InboxInvoiceOptionsEnum.PREVIEW}>
                    <MenuItemContent icon={<EyeNoBgIcon size={14} />} text={t("action:inbox_invoice.preview")} />
                </Menu.Item>
                {canAssignInboxInvoice ? (
                    inboxInvoice.assignedTo ? (
                        <Menu.Item key={InboxInvoiceOptionsEnum.UNASSIGN} className="flex items-center min-w-190 py-8">
                            <div className="flex items-center">
                                <UnAssign size={14} />
                                <p className="leading-none ml-5">
                                    <span className="text-text"> {t("action:inbox_invoice.unassign")}</span>
                                </p>
                            </div>
                        </Menu.Item>
                    ) : (
                        <Menu.SubMenu
                            key={InboxInvoiceOptionsEnum.ASSIGN}
                            className="no-submenu-arrow no-arrow"
                            onTitleClick={handleSubMenuTitleClick}
                            title={<MenuItemContent icon={<Assign size={14} />} text={t("action:inbox_invoice.assign")} />}
                        >
                            <EmployeeList employees={possibleAssignees} keySelector={employeeKeySelector} onEmployeeSelect={onEmployeeSelect} />
                        </Menu.SubMenu>
                    )
                ) : null}

                {userCompanies.length > 0 && (
                    <Menu.SubMenu
                        key={InboxInvoiceOptionsEnum.MOVE}
                        popupOffset={[0, 0]}
                        className="no-submenu-arrow no-arrow"
                        onTitleClick={handleSubMenuTitleClick}
                        title={<MenuItemContent icon={<Move size={14} />} text={t("action:inbox_invoice.move")} />}
                    >
                        <SubCompanyList userCompanies={userCompanies} keySelector={menuKeySelector} onCompanySelect={(company) => moveInvoice(inboxInvoice, company)} />
                    </Menu.SubMenu>
                )}

                <Menu.Item key={InboxInvoiceOptionsEnum.DOWNLOAD}>
                    <MenuItemContent icon={<DownloadIcon size={14} />} text={t("action:inbox_invoice.download")} />
                </Menu.Item>

                {canWriteInboxInvoice && (
                    <Menu.Item key={InboxInvoiceOptionsEnum.REMOVE}>
                        <MenuItemContent icon={<TrashIcon size={14} />} text={t("action:inbox_invoice.delete")} />
                    </Menu.Item>
                )}
            </Menu>
        )
    }

    return (
        <Dropdown
            overlay={isInvoiceActionDropdownOpen ? dropDownOverlay(onEmployeeSelect) : <></>}
            open={isInvoiceActionDropdownOpen}
            onOpenChange={(flag) => setIsInvoiceActionDropdownOpen(flag)}
            trigger={["click"]}
            placement="bottomRight"
        >
            <Button onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()} className="m-5 ant-btn-primary">
                <span>{t("label:options")} </span>
                <CaretDownOutlined className="text-sm" />
            </Button>
        </Dropdown>
    )
}
